import React, { useState, useEffect } from 'react';
import { Users, PenSquare, Save, X, Plus, Trash2 } from 'lucide-react';
import { actualizarMatricula } from '../../services/matriculaService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DatosPersonales = ({ matricula }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [esMayorA9Meses, setEsMayorA9Meses] = useState(false);
  const [noCedula, setNoCedula] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    nombre: matricula.nino.nombre,
    apellidos: matricula.nino.apellidos,
    cedula: matricula.nino.cedula || '',
    fecha_nacimiento: matricula.nino.fecha_nacimiento,
    lugar_nacimiento: matricula.nino.lugar_nacimiento || '',
    direccion: matricula.nino.direccion || '',
    numero_hermanos_varones: matricula.nino.numero_hermanos_varones || 0,
    numero_hermanos_mujeres: matricula.nino.numero_hermanos_mujeres || 0,
    seccion: matricula.nino.seccion || '',
    vive_con: matricula.nino.vive_con || '',
    pasa_tiempo_con: matricula.nino.pasa_tiempo_con || '',
    observaciones_entorno: matricula.nino.observaciones_entorno || '',
    hermanos: matricula.hermanos || []
  });

  const calcularEdadEnMeses = (fechaNacimiento) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    const diferenciaMeses = (hoy.getFullYear() - fechaNac.getFullYear()) * 12 + 
                           (hoy.getMonth() - fechaNac.getMonth());
    return diferenciaMeses;
  };

  useEffect(() => {
    const edadMeses = calcularEdadEnMeses(formData.fecha_nacimiento);
    setEsMayorA9Meses(edadMeses > 9);
  }, [formData.fecha_nacimiento]);

  const validarCedulaEcuatoriana = (cedula) => {
    if (!/^\d{10}$/.test(cedula)) return false;

    const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    const provincia = parseInt(cedula.substring(0, 2));
    if (provincia < 1 || provincia > 24) return false;

    const tercerDigito = parseInt(cedula.charAt(2));
    if (tercerDigito > 6) return false;

    let suma = 0;
    for (let i = 0; i < 9; i++) {
      let valor = parseInt(cedula.charAt(i)) * coeficientes[i];
      if (valor > 9) valor -= 9;
      suma += valor;
    }

    const digitoVerificador = (10 - (suma % 10)) % 10;
    return digitoVerificador === parseInt(cedula.charAt(9));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validarForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }

    if (!formData.apellidos.trim()) {
      newErrors.apellidos = 'Los apellidos son obligatorios';
    }

    if (!formData.direccion.trim()) {
      newErrors.direccion = 'La dirección es obligatoria';
    }

    if (!formData.fecha_nacimiento) {
      newErrors.fecha_nacimiento = 'La fecha de nacimiento es obligatoria';
    } else {
      const fechaNac = new Date(formData.fecha_nacimiento);
      const hoy = new Date();
      if (fechaNac > hoy) {
        newErrors.fecha_nacimiento = 'La fecha de nacimiento no puede ser mayor a la actual';
      }
    }

    if (esMayorA9Meses && !noCedula) {
      if (!formData.cedula) {
        newErrors.cedula = 'La cédula es obligatoria para niños mayores de 9 meses';
      } else if (!validarCedulaEcuatoriana(formData.cedula)) {
        newErrors.cedula = 'La cédula ingresada no es una cédula ecuatoriana válida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleHermanoChange = (index, field, value) => {
    const newHermanos = [...formData.hermanos];
    newHermanos[index] = {
      ...newHermanos[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      hermanos: newHermanos
    }));
  };

  const agregarHermano = () => {
    setFormData(prev => ({
      ...prev,
      hermanos: [...prev.hermanos, { nombre: '', edad: '', lugar_ocupa: '' }]
    }));
  };

  const eliminarHermano = (index) => {
    setFormData(prev => ({
      ...prev,
      hermanos: prev.hermanos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!validarForm()) {
        return;
      }

      const datosActualizados = {
        ...formData
      };

      if (!esMayorA9Meses || noCedula) {
        delete datosActualizados.cedula;
      }

      await actualizarMatricula(matricula.nino.id_child, {
        datosNino: datosActualizados,
        hermanos: formData.hermanos
      });

      setIsEditing(false);
      Object.assign(matricula.nino, datosActualizados);
      matricula.hermanos = formData.hermanos;
      toast.success('Datos personales actualizados exitosamente', {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
    } catch (error) {
      console.error('Error al actualizar:', error);
      if (error.message === "La cédula ya está registrada para otro niño") {
        setErrors(prev => ({...prev, cedula: 'La cédula ya está registrada para otro niño'}));
        toast.error('La cédula ya está registrada para otro niño', {
          position: 'top-right',
          autoClose: 3000,
          style: { marginTop: '10px' }
        });
      } else {
        toast.error('Error al actualizar los datos: ' + error.message, {
          position: 'top-right',
          autoClose: 3000,
          style: { marginTop: '10px' }
        });
      }
    }
  };

  const inputStyle = "w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";
  const errorStyle = "border-red-300 focus:ring-red-500 focus:border-red-500";

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Users className="mr-2 text-indigo-500" size={20} />
          Información del Niño
        </h3>
        {!isEditing ? (
          <PenSquare 
            className="text-indigo-500 cursor-pointer hover:text-indigo-600" 
            size={20} 
            onClick={() => setIsEditing(true)}
          />
        ) : (
          <div className="flex gap-2">
            <Save 
              className="text-green-500 cursor-pointer hover:text-green-600" 
              size={20} 
              onClick={handleSubmit}
            />
            <X 
              className="text-red-500 cursor-pointer hover:text-red-600" 
              size={20} 
              onClick={() => setIsEditing(false)}
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        {Object.entries({
          'Nombre completo:': isEditing ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className={`${inputStyle} ${errors.nombre ? errorStyle : ''}`}
                  placeholder="Nombre"
                />
                {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>}
              </div>
              <div className="flex-1">
                <input
                  name="apellidos"
                  value={formData.apellidos}
                  onChange={handleInputChange}
                  className={`${inputStyle} ${errors.apellidos ? errorStyle : ''}`}
                  placeholder="Apellidos"
                />
                {errors.apellidos && <p className="text-red-500 text-sm mt-1">{errors.apellidos}</p>}
              </div>
            </div>
          ) : `${matricula.nino.nombre} ${matricula.nino.apellidos}`,
          'Fecha de nacimiento:': isEditing ? (
            <div>
              <input
                type="date"
                name="fecha_nacimiento"
                value={formData.fecha_nacimiento.split('T')[0]}
                onChange={handleInputChange}
                className={`${inputStyle} ${errors.fecha_nacimiento ? errorStyle : ''}`}
                required
              />
              {errors.fecha_nacimiento && 
                <p className="text-red-500 text-sm mt-1">{errors.fecha_nacimiento}</p>
              }
              {esMayorA9Meses && 
                <div className="mt-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={noCedula}
                      onChange={(e) => setNoCedula(e.target.checked)}
                      className="form-checkbox"
                    />
                    <span className="text-sm text-gray-600">No tiene cédula</span>
                  </label>
                </div>
              }
            </div>
          ) : formatearFecha(matricula.nino.fecha_nacimiento),
          ...(esMayorA9Meses && !noCedula ? {
            'Cédula:': isEditing ? (
              <div>
                <input
                  name="cedula"
                  value={formData.cedula}
                  onChange={handleInputChange}
                  className={`${inputStyle} ${errors.cedula ? errorStyle : ''}`}
                  placeholder="Cédula (Obligatoria)"
                  maxLength="10"
                />
                {errors.cedula && 
                  <p className="text-red-500 text-sm mt-1">{errors.cedula}</p>
                }
              </div>
            ) : matricula.nino.cedula || 'No registrada'
          } : {}),
          'Lugar de nacimiento:': isEditing ? (
            <input
              name="lugar_nacimiento"
              value={formData.lugar_nacimiento}
              onChange={handleInputChange}
              className={inputStyle}
              placeholder="Lugar de nacimiento"
            />
          ) : matricula.nino.lugar_nacimiento || 'Ninguno',
          'Dirección:': isEditing ? (
            <div>
              <input
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
                className={`${inputStyle} ${errors.direccion ? errorStyle : ''}`}
                placeholder="Dirección"
              />
              {errors.direccion && 
                <p className="text-red-500 text-sm mt-1">{errors.direccion}</p>
              }
            </div>
          ) : matricula.nino.direccion || 'Ninguna',
          'Número de hermanos:': isEditing ? (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm text-gray-500">Varones:</label>
                <input
                  type="number"
                  name="numero_hermanos_varones"
                  value={formData.numero_hermanos_varones}
                  onChange={handleInputChange}
                  className={inputStyle}
                  min="0"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500">Mujeres:</label>
                <input
                  type="number"
                  name="numero_hermanos_mujeres"
                  value={formData.numero_hermanos_mujeres}
                  onChange={handleInputChange}
                  className={inputStyle}
                  min="0"
                />
              </div>
            </div>
          ) : `Varones: ${matricula.nino.numero_hermanos_varones || 0}, Mujeres: ${matricula.nino.numero_hermanos_mujeres || 0}`,
          'Sección:': isEditing ? (
            <input
              name="seccion"
              value={formData.seccion}
              onChange={handleInputChange}
              className={inputStyle}
              placeholder="Sección"
            />
          ) : matricula.nino.seccion || 'No asignada',
          'Vive con:': isEditing ? (
            <input
              name="vive_con"
              value={formData.vive_con}
              onChange={handleInputChange}
              className={inputStyle}
              placeholder="Vive con"
            />
          ) : matricula.nino.vive_con || 'No especificado',
          'Pasa tiempo con:': isEditing ? (
            <input
              name="pasa_tiempo_con"
              value={formData.pasa_tiempo_con}
              onChange={handleInputChange}
              className={inputStyle}
              placeholder="Pasa tiempo con"
            />
          ) : matricula.nino.pasa_tiempo_con || 'No especificado',
          'Observaciones del entorno:': isEditing ? (
            <select
              name="observaciones_entorno"
              value={formData.observaciones_entorno}
              onChange={handleInputChange}
              className={inputStyle}
            >
              <option value="">Seleccione una opción</option>
              <option value="padres separados">Padres separados</option>
              <option value="divorciados">Divorciados</option>
              <option value="fallecidos">Fallecidos</option>
              <option value="otros compromisos">Otros compromisos</option>
            </select>
          ) : matricula.nino.observaciones_entorno || 'Ninguna'
        }).map(([label, value]) => (
          <div key={label} className="space-y-1">
            <span className="text-sm font-medium text-gray-500">{label}</span>
            <div className="text-gray-900">{value}</div>
          </div>
        ))}

        <div className="pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-500">Hermanos:</span>
            {isEditing && (
              <button
                onClick={agregarHermano}
                className="flex items-center text-indigo-600 hover:text-indigo-700"
              >
                <Plus size={16} className="mr-1" />
                Agregar hermano
              </button>
            )}
          </div>
          {formData.hermanos && formData.hermanos.length > 0 ? (
            <div className="space-y-3 text-sm">
              {formData.hermanos.map((hermano, index) => (
                <div key={index} className={`${isEditing ? 'flex gap-2 items-start' : ''}`}>
                  {isEditing ? (
                    <>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={hermano.nombre}
                          onChange={(e) => handleHermanoChange(index, 'nombre', e.target.value)}
                          className={`${inputStyle} w-40`}
                          placeholder="Nombre del hermano"
                        />
                      </div>
                      <input
                        type="number"
                        value={hermano.edad}
                        onChange={(e) => handleHermanoChange(index, 'edad', e.target.value)}
                        className={`${inputStyle} `}
                        placeholder="edad"
                      />
                      <input
                        type="number"
                        value={hermano.lugar_ocupa}
                        onChange={(e) => handleHermanoChange(index, 'lugar_ocupa', e.target.value)}
                        className={`${inputStyle} `}
                        placeholder="Lugar"
                      />
                      <button
                        onClick={() => eliminarHermano(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={20} />
                      </button>
                    </>
                  ) : (
                    <div className="text-gray-900">
                      {hermano.nombre} - {hermano.edad} años (Lugar que ocupa: {hermano.lugar_ocupa})
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No tiene hermanos registrados</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatosPersonales;
